import { useState, useEffect } from "react";
import GameLayout from "../../components/GameLayout";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, Edit2, Trash2, Save, X, Receipt } from "lucide-react";

const METHODS = ["FaucetPay", "External Wallet", "Manual", "Bank Transfer", "Crypto Direct"];
const CURRENCIES = ["BTC", "LTC", "DOGE", "TRX", "ETH", "USDT", "BNB"];

const blankEntry = () => ({
  type: "deposit", username: "", address: "", method: "FaucetPay",
  currency: "BTC", amount: "", created_at: ""
});

export default function AdminPaymentProofs() {
  const [settings, setSettings] = useState({
    page_enabled: false, ticker_enabled: false, landing_preview_enabled: false, counter_enabled: false,
    counter_amount_label: "", counter_count_label: "", counter_amount_period: "this month",
  });
  const [entries, setEntries] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(blankEntry());
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState(blankEntry());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await API.get("/admin/payment-proofs");
      setSettings(data.settings || settings);
      setEntries(data.manual_entries || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const saveSettings = async (next) => {
    setBusy(true);
    try {
      const { data } = await API.put("/admin/payment-proofs/settings", next);
      setSettings(data.settings || next);
      toast.success("Saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = (key) => saveSettings({ ...settings, [key]: !settings[key] });

  const saveSettingFields = async () => {
    await saveSettings(settings);
  };

  const createEntry = async (e) => {
    e?.preventDefault?.();
    if (!addForm.username || !addForm.amount) return toast.error("Username and amount required");
    setBusy(true);
    try {
      const payload = { ...addForm, amount: parseFloat(addForm.amount) };
      if (!payload.created_at) delete payload.created_at;
      await API.post("/admin/payment-proofs", payload);
      setAdding(false); setAddForm(blankEntry());
      await load();
      toast.success("Entry added");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const startEdit = (e) => {
    setEditingId(e.proof_id);
    setEditForm({
      type: e.type, username: e.username, address: e.address, method: e.method,
      currency: e.currency, amount: e.amount, created_at: e.created_at || ""
    });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = { ...editForm, amount: parseFloat(editForm.amount) };
      await API.put(`/admin/payment-proofs/${editingId}`, payload);
      setEditingId(null);
      await load();
      toast.success("Updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const deleteEntry = async (proof_id) => {
    if (!window.confirm("Delete this entry?")) return;
    setBusy(true);
    try {
      await API.delete(`/admin/payment-proofs/${proof_id}`);
      await load();
      toast.success("Deleted");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const ToggleRow = ({ k, label, hint }) => (
    <div className="flex items-center justify-between py-3 border-b border-[#2A3241]/40 last:border-b-0">
      <div className="min-w-0">
        <p className="text-sm font-bold text-[#F8FAFC]">{label}</p>
        {hint && <p className="text-xs text-[#64748B] mt-0.5">{hint}</p>}
      </div>
      <button onClick={() => toggle(k)} disabled={busy} data-testid={`toggle-${k}`}
        className={`px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-md border ${settings[k] ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-[#2A3241] text-[#64748B]"}`}>
        {settings[k] ? "ENABLED" : "DISABLED"}
      </button>
    </div>
  );

  return (
    <GameLayout title="Payment Proofs">
      <div className="max-w-5xl space-y-6" data-testid="admin-payment-proofs-page">
        {/* Settings card */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241] p-6">
          <div className="flex items-center gap-3 mb-4">
            <Receipt className="w-5 h-5 text-[#FFD600]" />
            <h2 className="text-lg font-bold text-[#F8FAFC]">Visibility Settings</h2>
          </div>
          <ToggleRow k="page_enabled" label="Public /payment-proofs page" hint="Enables the dedicated public page and footer link." />
          <ToggleRow k="ticker_enabled" label="Live ticker bar on landing page" hint="Thin scrolling line above the hero showing latest payouts." />
          <ToggleRow k="landing_preview_enabled" label="Recent Payouts section on landing page" hint="Compact 5+5 cards below CTA, with link to full page." />
          <ToggleRow k="counter_enabled" label="Total paid counter on landing page" hint="Big stats card showing the totals you set below." />

          {settings.counter_enabled && (
            <div className="mt-4 pt-4 border-t border-[#2A3241]/40 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-[#94A3B8] text-xs">Amount label</Label>
                <Input value={settings.counter_amount_label} onChange={(e) => setSettings({ ...settings, counter_amount_label: e.target.value })}
                  placeholder="$12,485" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="counter-amount-input" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Period</Label>
                <Input value={settings.counter_amount_period} onChange={(e) => setSettings({ ...settings, counter_amount_period: e.target.value })}
                  placeholder="this month" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="counter-period-input" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Withdrawals count label</Label>
                <Input value={settings.counter_count_label} onChange={(e) => setSettings({ ...settings, counter_count_label: e.target.value })}
                  placeholder="1,247" className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="counter-count-input" />
              </div>
              <div className="sm:col-span-3">
                <button onClick={saveSettingFields} disabled={busy} data-testid="save-counter-btn"
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-md hover:bg-[#FFD600]/90 disabled:opacity-50">
                  Save Counter Values
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Entries card */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241] p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-[#F8FAFC]">Manual Entries</h2>
              <p className="text-xs text-[#64748B] mt-0.5">Real approved transactions auto-show alongside these. Users see one merged list.</p>
            </div>
            <button onClick={() => setAdding(!adding)} data-testid="add-entry-btn"
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#10B981] text-[#0B0E14] rounded-md hover:bg-[#10B981]/90">
              {adding ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {adding ? "Cancel" : "Add Entry"}
            </button>
          </div>

          {adding && (
            <form onSubmit={createEntry} className="bg-[#0B0E14] rounded-lg border border-[#10B981]/30 p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="add-entry-form">
              <div>
                <Label className="text-[#94A3B8] text-xs">Type</Label>
                <select value={addForm.type} onChange={(e) => setAddForm({ ...addForm, type: e.target.value })} data-testid="add-type"
                  className="w-full h-10 mt-1 bg-[#131722] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                  <option value="deposit">Deposit</option>
                  <option value="withdrawal">Withdrawal</option>
                </select>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Username</Label>
                <Input value={addForm.username} onChange={(e) => setAddForm({ ...addForm, username: e.target.value })} placeholder="cryptoking" required
                  className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="add-username" />
              </div>
              <div className="lg:col-span-2">
                <Label className="text-[#94A3B8] text-xs">TXID (deposit) or Wallet address (withdrawal)</Label>
                <Input value={addForm.address} onChange={(e) => setAddForm({ ...addForm, address: e.target.value })} placeholder="7a3f9b...e891 (TXID) or bc1q... (wallet)"
                  className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] mt-1 font-mono-game text-xs" data-testid="add-address" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Method</Label>
                <select value={addForm.method} onChange={(e) => setAddForm({ ...addForm, method: e.target.value })} data-testid="add-method"
                  className="w-full h-10 mt-1 bg-[#131722] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                  {METHODS.map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Currency</Label>
                <select value={addForm.currency} onChange={(e) => setAddForm({ ...addForm, currency: e.target.value })} data-testid="add-currency"
                  className="w-full h-10 mt-1 bg-[#131722] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Amount (USD)</Label>
                <Input type="number" step="0.01" value={addForm.amount} onChange={(e) => setAddForm({ ...addForm, amount: e.target.value })} placeholder="25.00" required
                  className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] mt-1 font-mono-game" data-testid="add-amount" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs">Date (optional, blank=now)</Label>
                <Input type="datetime-local" value={addForm.created_at ? addForm.created_at.slice(0, 16) : ""}
                  onChange={(e) => setAddForm({ ...addForm, created_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                  className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="add-date" />
              </div>
              <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
                <button type="submit" disabled={busy} data-testid="submit-add-entry-btn"
                  className="flex items-center gap-1.5 px-5 py-2 text-xs font-bold uppercase tracking-widest bg-[#10B981] text-[#0B0E14] rounded-md hover:bg-[#10B981]/90 disabled:opacity-50">
                  <Save className="w-3.5 h-3.5" /> Save Entry
                </button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0B0E14]">
                <tr className="text-[10px] uppercase tracking-widest text-[#64748B]">
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">TX / Address</th>
                  <th className="px-3 py-2 text-left">Method</th>
                  <th className="px-3 py-2 text-left">Cur</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-[#64748B] text-xs">No manual entries yet. Real approved transactions still auto-display on the public page.</td></tr>
                )}
                {entries.map((e) => editingId === e.proof_id ? (
                  <tr key={e.proof_id} className="border-t border-[#2A3241]/40 bg-[#FFD600]/5">
                    <td className="px-2 py-2"><select value={editForm.type} onChange={(ev) => setEditForm({ ...editForm, type: ev.target.value })} className="w-full h-8 bg-[#131722] border border-[#2A3241] text-[#F8FAFC] rounded text-xs px-2"><option value="deposit">Deposit</option><option value="withdrawal">Withdrawal</option></select></td>
                    <td className="px-2 py-2"><Input value={editForm.username} onChange={(ev) => setEditForm({ ...editForm, username: ev.target.value })} className="h-8 bg-[#131722] border-[#2A3241] text-[#F8FAFC] text-xs" /></td>
                    <td className="px-2 py-2"><Input value={editForm.address} onChange={(ev) => setEditForm({ ...editForm, address: ev.target.value })} className="h-8 bg-[#131722] border-[#2A3241] text-[#F8FAFC] text-xs font-mono-game" /></td>
                    <td className="px-2 py-2"><select value={editForm.method} onChange={(ev) => setEditForm({ ...editForm, method: ev.target.value })} className="w-full h-8 bg-[#131722] border border-[#2A3241] text-[#F8FAFC] rounded text-xs px-2">{METHODS.map(m => <option key={m}>{m}</option>)}</select></td>
                    <td className="px-2 py-2"><select value={editForm.currency} onChange={(ev) => setEditForm({ ...editForm, currency: ev.target.value })} className="w-full h-8 bg-[#131722] border border-[#2A3241] text-[#F8FAFC] rounded text-xs px-2">{CURRENCIES.map(c => <option key={c}>{c}</option>)}</select></td>
                    <td className="px-2 py-2"><Input type="number" step="0.01" value={editForm.amount} onChange={(ev) => setEditForm({ ...editForm, amount: ev.target.value })} className="h-8 bg-[#131722] border-[#2A3241] text-[#F8FAFC] text-xs font-mono-game text-right" /></td>
                    <td className="px-2 py-2"><Input type="datetime-local" value={editForm.created_at ? editForm.created_at.slice(0, 16) : ""} onChange={(ev) => setEditForm({ ...editForm, created_at: ev.target.value ? new Date(ev.target.value).toISOString() : "" })} className="h-8 bg-[#131722] border-[#2A3241] text-[#F8FAFC] text-xs" /></td>
                    <td className="px-2 py-2 text-right">
                      <button onClick={saveEdit} disabled={busy} data-testid={`save-edit-${e.proof_id}`} className="px-2 py-1 text-[10px] font-bold bg-[#10B981] text-[#0B0E14] rounded mr-1"><Save className="w-3 h-3 inline" /></button>
                      <button onClick={() => setEditingId(null)} className="px-2 py-1 text-[10px] font-bold border border-[#2A3241] text-[#94A3B8] rounded"><X className="w-3 h-3 inline" /></button>
                    </td>
                  </tr>
                ) : (
                  <tr key={e.proof_id} className="border-t border-[#2A3241]/40 hover:bg-[#1C212E]/40" data-testid={`entry-row-${e.proof_id}`}>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${e.type === "deposit" ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#FFD600]/10 text-[#FFD600]"}`}>{e.type}</span>
                    </td>
                    <td className="px-3 py-2 text-[#F8FAFC]">{e.username}</td>
                    <td className="px-3 py-2 font-mono-game text-xs text-[#94A3B8] truncate max-w-[160px]">{e.address || "—"}</td>
                    <td className="px-3 py-2 text-[#94A3B8]">{e.method}</td>
                    <td className="px-3 py-2 font-mono-game text-[#FFD600]">{e.currency}</td>
                    <td className="px-3 py-2 text-right font-mono-game font-bold text-[#F8FAFC]">${(e.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs text-[#64748B]">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => startEdit(e)} data-testid={`edit-${e.proof_id}`} className="px-2 py-1 text-[10px] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded mr-1"><Edit2 className="w-3 h-3 inline" /></button>
                      <button onClick={() => deleteEntry(e.proof_id)} data-testid={`delete-${e.proof_id}`} className="px-2 py-1 text-[10px] text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><Trash2 className="w-3 h-3 inline" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </GameLayout>
  );
}
